if (!customElements.get('catalog-pagination')) {
  class CatalogPagination extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.requestId = 0;
      this.loading = false;
      this.observer = null;
      this.promoCardHTML = null;
      this.promoCardIndex = -1;
    }

    connectedCallback() {
      window.setTimeout(() => this.sync(), 0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.disconnectObserver();
    }

    get mode() {
      return this.dataset.mode === 'infinite'
        ? 'infinite'
        : 'products_per_page';
    }

    get container() {
      return this.querySelector('#ProductGridContainer');
    }

    get grid() {
      return this.querySelector('#product-grid');
    }

    get meta() {
      return this.querySelector('[data-catalog-page-meta]');
    }

    getActiveDesktopColumns() {
      const collectionView = document.querySelector('collection-view');
      const activeColumns = collectionView?.getActiveColumns?.('desktop');

      return activeColumns || this.grid?.dataset.cols || this.dataset.defaultColumns || '5';
    }

    getPageSize(columns = this.getActiveDesktopColumns()) {
      return columns === '5' ? 40 : 36;
    }

    getRequestedPage() {
      const page = Number(new URL(window.location.href).searchParams.get('page'));

      return Number.isInteger(page) && page > 0 ? page : 1;
    }

    getEndpointURL(page, columns) {
      const url = new URL(window.location.href);

      url.searchParams.delete('section_id');
      url.searchParams.set('view', `olas-grid-${columns}`);

      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }

      return url;
    }

    resetPageURL() {
      const url = new URL(window.location.href);

      url.searchParams.delete('page');
      url.searchParams.delete('view');

      history.replaceState(
        { ...(history.state || {}), searchParams: url.searchParams.toString() },
        '',
        url
      );
    }

    capturePromoCard() {
      if (this.promoCardHTML !== null || !this.grid) return;

      const promoCard = this.grid.querySelector('.js-card-text');
      this.promoCardHTML = promoCard?.outerHTML || '';
      this.promoCardIndex = promoCard
        ? Array.from(this.grid.children).indexOf(promoCard)
        : -1;
    }

    restorePromoCard() {
      if (!this.promoCardHTML || !this.grid) return;

      const template = document.createElement('template');
      template.innerHTML = this.promoCardHTML.trim();
      const promoCard = template.content.firstElementChild;
      const target = this.grid.children[this.promoCardIndex];

      if (!promoCard) return;
      if (target) {
        target.before(promoCard);
      } else {
        this.grid.append(promoCard);
      }
    }

    async sync({ force = false, resetPage = false, columns } = {}) {
      if (!this.container || !this.grid || !this.meta) return;

      this.capturePromoCard();

      const activeColumns = columns || this.getActiveDesktopColumns();
      const desiredPageSize = this.getPageSize(activeColumns);
      let requestedPage = this.getRequestedPage();

      if (resetPage || (this.mode === 'infinite' && requestedPage > 1)) {
        requestedPage = 1;
        this.resetPageURL();
      }

      const renderedColumns = this.meta.dataset.renderedColumns;
      const renderedPageSize = Number(this.meta.dataset.pageSize);
      const renderedPage = Number(this.meta.dataset.currentPage);
      const needsRefresh =
        force ||
        renderedColumns !== activeColumns ||
        renderedPageSize !== desiredPageSize ||
        renderedPage !== requestedPage;

      if (needsRefresh) {
        await this.fetchPage(requestedPage, activeColumns);
        return;
      }

      document.querySelector('collection-view')?.applyPreferencesToGrid?.(this.grid);
      this.configureMode();
    }

    async fetchPage(page, columns, { append = false } = {}) {
      if (this.loading && append) return;

      this.abortController?.abort();

      const abortController = new AbortController();
      const requestId = ++this.requestId;
      this.abortController = abortController;
      this.loading = true;
      this.setLoading(true);

      try {
        const response = await fetch(this.getEndpointURL(page, columns), {
          signal: abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Catalog page request failed with status ${response.status}`);
        }

        const responseText = await response.text();

        if (
          abortController.signal.aborted ||
          requestId !== this.requestId ||
          !this.isConnected
        ) {
          return;
        }

        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const payload = responseHTML.querySelector('#CatalogPagePayload');

        if (!payload) {
          throw new Error('Catalog page response did not contain a product grid.');
        }

        if (append) {
          this.appendPayload(payload);
        } else {
          this.replacePayload(payload);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to update catalog pagination:', error);
          this.setStatus('Unable to load more products. Please try again.');
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
          this.setLoading(false);
          this.abortController = null;
          this.configureMode();
        }
      }
    }

    replacePayload(payload) {
      this.container.innerHTML = payload.innerHTML;

      document.querySelector('collection-view')?.applyPreferencesToGrid?.(this.grid);
      this.restorePromoCard();
      this.sanitizePaginationLinks();
    }

    appendPayload(payload) {
      const sourceGrid = payload.querySelector('#product-grid');
      const sourceMeta = payload.querySelector('[data-catalog-page-meta]');

      if (!sourceGrid || !sourceMeta || !this.grid || !this.meta) return;

      const fragment = document.createDocumentFragment();
      Array.from(sourceGrid.children).forEach(item => fragment.append(item));
      this.grid.append(fragment);

      this.meta.dataset.currentPage = sourceMeta.dataset.currentPage;
      this.meta.dataset.pages = sourceMeta.dataset.pages;
      this.meta.dataset.hasNext = sourceMeta.dataset.hasNext;
    }

    sanitizePaginationLinks() {
      this.querySelectorAll('[data-catalog-pagination] a[href]').forEach(link => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.delete('view');
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    configureMode() {
      const pagination = this.querySelector('[data-catalog-pagination]');
      const infiniteControls = this.querySelector('[data-catalog-infinite]');

      this.sanitizePaginationLinks();

      if (this.mode !== 'infinite') {
        if (pagination) pagination.hidden = false;
        if (infiniteControls) infiniteControls.hidden = true;
        this.disconnectObserver();
        return;
      }

      if (pagination) pagination.hidden = true;

      const hasNext = this.meta?.dataset.hasNext === 'true';
      if (infiniteControls) infiniteControls.hidden = !hasNext;

      if (hasNext) {
        this.observeInfiniteScroll();
      } else {
        this.disconnectObserver();
      }
    }

    observeInfiniteScroll() {
      const sentinel = this.querySelector('[data-catalog-infinite-sentinel]');
      if (!sentinel || !('IntersectionObserver' in window)) return;

      this.disconnectObserver();
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadNextPage();
        }
      }, { rootMargin: '500px 0px' });
      this.observer.observe(sentinel);
    }

    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    }

    loadNextPage() {
      if (this.loading || !this.meta) return;

      const currentPage = Number(this.meta.dataset.currentPage);
      const pages = Number(this.meta.dataset.pages);

      if (!Number.isInteger(currentPage) || currentPage >= pages) {
        this.configureMode();
        return;
      }

      this.disconnectObserver();
      this.fetchPage(currentPage + 1, this.getActiveDesktopColumns(), { append: true });
    }

    setLoading(isLoading) {
      this.container?.toggleAttribute('aria-busy', isLoading);
      const spinner = this.querySelector('[data-catalog-infinite-spinner]');
      if (spinner) spinner.hidden = !isLoading;
    }

    setStatus(message) {
      const status = this.querySelector('[data-catalog-infinite-status]');
      if (status) status.textContent = message;
    }

    onGridChange(columns) {
      if (!['3', '4', '5'].includes(columns)) return;

      this.sync({ force: true, resetPage: true, columns });
    }

    resetAfterContentUpdate() {
      window.setTimeout(() => this.sync(), 0);
    }
  }

  customElements.define('catalog-pagination', CatalogPagination);
}
